import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import usePageMeta from '../hooks/usePageMeta';

const StatutoryWarnings = () => (
  <section className="legal-section">
    <h2>Statutory Warnings</h2>
    <div className="warning-box">
      <p className="warning-text-legal">CONSUMPTION OF ALCOHOL IS INJURIOUS TO HEALTH</p>
      <p className="warning-text-legal">BE SAFE — DON'T DRINK AND DRIVE</p>
      <p className="warning-text-legal">DRINK RESPONSIBLY</p>
    </div>
  </section>
);

const CompanyInfo = () => (
  <section className="legal-section">
    <h2>Company Information</h2>
    <p><strong>Legal Entity:</strong> Hopfenzauber No-Belly Bier Pvt. Ltd.</p>
    <p><strong>Registered Office:</strong> Innov8 Orchid Center, 3rd Floor, Golf Course Road, Sector 53, Gurgaon, Haryana 122002</p>
    <p><strong>Contact:</strong> +91 9999307127 | contact@nobelly.in</p>
  </section>
);

const LegalNav = ({ active }) => (
  <nav className="legal-nav" aria-label="Legal navigation">
    <Link to="/privacy" className={`legal-nav-link ${active === 'privacy' ? 'active' : ''}`} data-testid="legal-nav-privacy">Privacy Policy</Link>
    <Link to="/terms" className={`legal-nav-link ${active === 'terms' ? 'active' : ''}`} data-testid="legal-nav-terms">Terms of Use</Link>
    <Link to="/responsible-drinking" className={`legal-nav-link ${active === 'responsible' ? 'active' : ''}`} data-testid="legal-nav-responsible">Responsible Drinking</Link>
  </nav>
);

export const PrivacyPolicy = () => {
  usePageMeta({
    title: 'Privacy Policy | No-Belly Bier',
    description: 'How No-Belly Bier collects, uses, retains and protects the personal information you provide via our newsletter, store-locator and HORECA partnership forms.',
    path: '/privacy',
  });
  return (
    <div className="legal-page" data-testid="page-privacy">
      <div className="legal-container">
        <div className="legal-header">
          <AlertTriangle size={40} color="#FFD700" aria-hidden="true" />
          <h1>Privacy Policy</h1>
        </div>
        <LegalNav active="privacy" />

        <section className="legal-section">
          <h2>Data We Collect</h2>
          <p>We collect only the information you voluntarily provide when you submit one of our forms.</p>
          <p><strong>Newsletter form:</strong></p>
          <ul className="legal-list">
            <li>Name, email address and city</li>
            <li>Consent flag and consent timestamp</li>
            <li>Source page on our site (e.g. "home/community")</li>
          </ul>
          <p><strong>Store-locator inquiry form (Find Us):</strong></p>
          <ul className="legal-list">
            <li>Name, phone number, city and optional message</li>
            <li>Consent flag and consent timestamp</li>
          </ul>
          <p><strong>HORECA partnership inquiry form:</strong></p>
          <ul className="legal-list">
            <li>Contact name, outlet / company name, outlet type</li>
            <li>Phone number, email address, city</li>
            <li>Number of outlets and optional message</li>
            <li>Consent flag, consent timestamp and lead source</li>
          </ul>
          <p>We do not knowingly collect data from anyone under 21. We do not collect location, payment or health information.</p>
        </section>

        <section className="legal-section">
          <h2>How We Use It</h2>
          <ul className="legal-list">
            <li>To respond to your inquiry (store locator or HORECA partnership)</li>
            <li>To send occasional email updates on launches, new outlets and events (newsletter subscribers only)</li>
            <li>To provide internal aggregate reporting on outlet demand and campaign performance</li>
          </ul>
          <p>We do not sell or rent your data. We do not use it for advertising retargeting.</p>
        </section>

        <section className="legal-section">
          <h2>Service Providers &amp; Data Processors</h2>
          <p>
            To run this website and process your submissions, we use a limited set of third-party service providers
            who process data strictly on our behalf under contract. These processors may include:
          </p>
          <ul className="legal-list">
            <li>Cloud hosting and content delivery for this website</li>
            <li>Managed database services for storing your submission</li>
            <li>Transactional email providers used to acknowledge inquiries or send newsletters</li>
            <li>Customer-relationship / lead-management tools used internally to reply to you</li>
            <li>Consent-based analytics (when installed, disclosed here and only after your consent)</li>
          </ul>
          <p>
            Processors are contractually bound to use your data only to provide the service to us, and never for
            their own purposes. Some processors may store data on servers located outside India; in that case we
            rely on standard contractual clauses to safeguard cross-border transfers.
          </p>
        </section>

        <section className="legal-section">
          <h2>Retention</h2>
          <ul className="legal-list">
            <li>Newsletter subscribers: retained until you unsubscribe, then deleted within 30 days.</li>
            <li>Store-locator inquiries: retained for up to 24 months from submission, then deleted or anonymised.</li>
            <li>HORECA partnership inquiries: retained for up to 36 months from submission (partnership lifecycle), then deleted or anonymised.</li>
            <li>Backups: purged on our standard rotation, typically within 90 days of the primary deletion.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>Your Rights — Access, Deletion, Correction, Withdrawal, Unsubscribe</h2>
          <p>You may, at any time, ask us to:</p>
          <ul className="legal-list">
            <li>Confirm what data we hold about you</li>
            <li>Correct any inaccurate data</li>
            <li>Delete your data</li>
            <li>Withdraw your consent (which stops all future processing except what is legally required to keep)</li>
            <li>Unsubscribe from newsletters — every newsletter email will include a one-click unsubscribe link</li>
          </ul>
          <p>
            To exercise any of these rights, email <a className="nb-inline-link" href="mailto:contact@nobelly.in">contact@nobelly.in</a> from
            the email address associated with the submission. We will respond within 30 days.
          </p>
        </section>

        <section className="legal-section">
          <h2>Grievance Officer</h2>
          <p>
            If you are unhappy with how we have handled your personal data or any request under this policy, you may
            contact our grievance officer:
          </p>
          <p>
            <strong>Grievance Officer, Hopfenzauber No-Belly Bier Pvt. Ltd.</strong><br />
            Innov8 Orchid Center, 3rd Floor, Golf Course Road, Sector 53, Gurgaon, Haryana 122002<br />
            Email: <a className="nb-inline-link" href="mailto:contact@nobelly.in">contact@nobelly.in</a><br />
            Phone: +91 9999307127
          </p>
          <p>We will acknowledge grievances within 3 business days and resolve them within 30 days.</p>
        </section>

        <section className="legal-section">
          <h2>Analytics &amp; Cookies</h2>
          <p>
            We use <strong>Google Analytics 4</strong> (Measurement ID G-SF97477760),
            loaded exclusively through <strong>Google Tag Manager</strong> (container GTM-56VGNV3C),
            to understand aggregated website usage. We use this data to improve the site, plan
            distribution and measure the effectiveness of our marketing campaigns.
          </p>
          <p><strong>What Analytics collects:</strong></p>
          <ul className="legal-list">
            <li>Aggregated website-usage reporting — pages viewed, time on page, referring source</li>
            <li>Approximate geographic reporting — country, state and city (derived from IP; the full IP is not stored by us)</li>
            <li>Device and browser type for compatibility reporting</li>
            <li>Marketing campaign attribution using UTM parameters (utm_source, utm_medium, utm_campaign, utm_content, utm_term)</li>
            <li>A first-party analytics cookie set only after you accept</li>
          </ul>
          <p><strong>What Analytics does NOT collect on our site:</strong></p>
          <ul className="legal-list">
            <li>Your name, email, phone number or any form message</li>
            <li>Advertising or cross-site retargeting identifiers</li>
            <li>Health, payment or government-ID information</li>
          </ul>
          <p><strong>Consent controls.</strong>{' '}
            When you first visit the site you will see a Cookies &amp; Analytics banner with{' '}
            <strong>Accept</strong> and <strong>Reject</strong> options. Analytics storage is
            <em> denied by default</em> under Google Consent Mode until you accept. Your choice is
            remembered on this device and you can change it at any time by clearing site data in
            your browser. Age verification is a separate control and does not constitute analytics
            consent.
          </p>
          <p>
            To learn more see Google's{' '}
            <a className="nb-inline-link" href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
              Analytics data policy
            </a>{' '}
            and{' '}
            <a className="nb-inline-link" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Google Privacy Policy
            </a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>Security</h2>
          <p>
            Data is transmitted over HTTPS. Access to submission databases is restricted to authorised employees on a
            need-to-know basis and protected by strong authentication. No system is 100% secure — please do not send
            sensitive information (payment cards, government IDs) via our forms.
          </p>
        </section>

        <CompanyInfo />
        <div className="legal-footer"><p>Last Updated: August 30, 2026</p></div>
      </div>
    </div>
  );
};

export const TermsOfUse = () => {
  usePageMeta({
    title: 'Terms of Use | No-Belly Bier',
    description: 'The terms governing use of the No-Belly Bier website, including age restrictions, information-only scope and trademark policy.',
    path: '/terms',
  });
  return (
    <div className="legal-page" data-testid="page-terms">
      <div className="legal-container">
        <div className="legal-header">
          <AlertTriangle size={40} color="#FFD700" aria-hidden="true" />
          <h1>Terms of Use</h1>
        </div>
        <LegalNav active="terms" />

        <section className="legal-section">
          <h2>Website Use</h2>
          <ul className="legal-list">
            <li>You must be 21 years of age or older to access this website.</li>
            <li>This website is informational only. No sale, delivery or e-commerce of alcoholic beverages is conducted through this website.</li>
            <li>Products are available for purchase only through licensed retail outlets and licensed HORECA venues in jurisdictions where the brand is distributed.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>Product Information &amp; Availability</h2>
          <p>Product formats and ABV as printed on approved labels:</p>
          <ul className="legal-list">
            <li>Low Calorie Mild Lager — &lt;5% ABV — 330 ml Pint, 650 ml Bottle, 500 ml Can (Available)</li>
            <li>Protein + Collagen Strong Lager — &lt;8% ABV — 650 ml Bottle, 500 ml Can (Available)</li>
            <li>Non-Alcoholic Lager — 0.0% ABV — 330 ml Pint (Coming Soon)</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>Trademark &amp; Intellectual Property</h2>
          <p>
            "No-Belly Bier", "Hopfenzauber", associated logos and brand elements are trademarks of
            Hopfenzauber No-Belly Bier Pvt. Ltd. Unauthorised use, reproduction or distribution is prohibited.
            Partner logos shown on this site are the property of their respective owners and used with permission.
          </p>
        </section>

        <CompanyInfo />
        <div className="legal-footer"><p>Last Updated: August 30, 2026</p></div>
      </div>
    </div>
  );
};

export const ResponsibleDrinking = () => {
  usePageMeta({
    title: 'Responsible Drinking | No-Belly Bier',
    description: 'Our commitment to responsible marketing, statutory warnings and adult-only communications.',
    path: '/responsible-drinking',
  });
  return (
    <div className="legal-page" data-testid="page-responsible">
      <div className="legal-container">
        <div className="legal-header">
          <AlertTriangle size={40} color="#FFD700" aria-hidden="true" />
          <h1>Responsible Drinking</h1>
        </div>
        <LegalNav active="responsible" />

        <StatutoryWarnings />

        <section className="legal-section">
          <h2>Our Commitment</h2>
          <ul className="legal-list">
            <li>We market our products only to adults of legal drinking age (21+ in Haryana).</li>
            <li>We do not depict binge drinking, driving after drinking, or drinking during pregnancy.</li>
            <li>All promotional activities comply with applicable state excise regulations.</li>
          </ul>
        </section>

        <CompanyInfo />
        <div className="legal-footer"><p>Last Updated: August 30, 2026</p></div>
      </div>
    </div>
  );
};

const Legal = () => {
  const location = useLocation();
  usePageMeta({
    title: 'Legal &amp; Compliance | No-Belly Bier',
    description: 'Legal, compliance and statutory information for the No-Belly Bier brand.',
    path: '/legal',
  });
  const active =
    location.pathname === '/terms' ? 'terms' :
    location.pathname === '/responsible-drinking' ? 'responsible' : 'privacy';

  return (
    <div className="legal-page" data-testid="page-legal">
      <div className="legal-container">
        <div className="legal-header">
          <AlertTriangle size={48} color="#FFD700" aria-hidden="true" />
          <h1>Legal &amp; Compliance</h1>
        </div>
        <LegalNav active={active} />
        <p className="legal-intro">Choose a section above.</p>
        <StatutoryWarnings />
        <CompanyInfo />
        <div className="legal-footer"><p>Last Updated: August 30, 2026</p></div>
      </div>
    </div>
  );
};

export default Legal;
